import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SupportChatService} from './SupportChatService';
import {normalizeImageToAttachment, previewUrlFor} from './supportChatImage';

// SupportComposer — textarea + adjuntar imagen + envío, con draft en localStorage.
//
// Props:
//   draftKey: clave del draft (conversationId o 'new').
//   disabled: fuerza estado no-editable (ej. hilo aún cargando — raramente usado).
//   onSend(message, attachmentBlob) → Promise<respuesta de la API>. El caller decide si appendear.
//     Debe devolver el shape estándar ({status:'OK'|'ERROR'|'NETWORK_ERROR', error_code, ...}).
//   onSent(response): callback tras un envío OK (para que el padre limpie/refetchee).
//   seed: {text, id} | null — el texto que pide un CTA («Extender el período de prueba», los de
//     upgrade). Llega por prop y no por el borrador: el editor restaura el borrador en su propio
//     efecto, que corre ANTES que los del panel, así que un borrador escrito por el panel llegaba
//     tarde y el editor abría vacío (C-fserb-d0-01). Al montar le gana al borrador; con el editor
//     ya montado (un `id` nuevo), sólo se pone si el editor está vacío: no pisa lo que se escribe.
//
// Reglas de envío:
//   - Enter envía, Shift+Enter hace salto de línea.
//   - Body requerido (el backend exige mensaje no vacío). Si hay adjunto sin texto, mostramos un
//     hint sutil pidiendo un mensaje — NO mandamos un body placeholder.
//   - Un solo adjunto por mensaje (v1).

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';
const DRAFT_DEBOUNCE_MS = 500;

export const SupportComposer = ({draftKey, disabled, onSend, onSent, seed = null}) => {
    const {t} = useTranslation();

    const [text, setText] = useState('');
    const textRef = useRef('');
    textRef.current = text;
    const seedAtMountRef = useRef(true);
    const [attachment, setAttachment] = useState(null); // { blob, filename }
    const [previewUrl, setPreviewUrl] = useState(null);
    const [sending, setSending] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [needsBodyHint, setNeedsBodyHint] = useState(false);

    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const draftTimerRef = useRef(null);

    // ── Draft: restore al montar / cambiar de key ──────────────────────────────
    useEffect(() => {
        const saved = SupportChatService.getDraft(draftKey);
        setText(saved || '');
        // Al cambiar de conversación, limpiamos adjunto/errores (no compartimos estado entre hilos).
        setAttachment(null);
        setErrorMessage('');
        setNeedsBodyHint(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftKey]);

    // El texto de un CTA (prop `seed`). Va DESPUÉS del restore del borrador: al montar, le gana.
    const seedId = seed ? seed.id : null;
    useEffect(() => {
        const atMount = seedAtMountRef.current;
        seedAtMountRef.current = false;
        if (!seed) return;
        if (!atMount && textRef.current.trim()) return;
        setText(seed.text);
        SupportChatService.saveDraft(draftKey, seed.text);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seedId]);

    // Limpieza de la object URL del preview al cambiarla / desmontar.
    useEffect(() => {
        return () => {
            if (previewUrl) {
                try {
                    URL.revokeObjectURL(previewUrl);
                } catch (_) { /* noop */ }
            }
        };
    }, [previewUrl]);

    useEffect(() => {
        return () => {
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        };
    }, []);

    // Autoexpansión simple del textarea (2-4 filas).
    const autoResize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const maxHeight = 120; // ~4 filas
        el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
    }, []);

    useEffect(() => {
        autoResize();
    }, [text, autoResize]);

    const scheduleDraftSave = useCallback((value) => {
        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        draftTimerRef.current = setTimeout(() => {
            SupportChatService.saveDraft(draftKey, value);
        }, DRAFT_DEBOUNCE_MS);
    }, [draftKey]);

    const handleChange = (e) => {
        const value = e.target.value;
        setText(value);
        setNeedsBodyHint(false);
        setErrorMessage('');
        scheduleDraftSave(value);
    };

    const setAttachmentWithPreview = (att) => {
        if (previewUrl) {
            try {
                URL.revokeObjectURL(previewUrl);
            } catch (_) { /* noop */ }
        }
        setAttachment(att);
        setPreviewUrl(att ? previewUrlFor(att) : null);
    };

    const handleFilePicked = async (file) => {
        if (!file) return;
        const att = await normalizeImageToAttachment(file);
        if (att) setAttachmentWithPreview(att);
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files && e.target.files[0];
        handleFilePicked(file);
        // Reset para poder re-seleccionar el mismo archivo.
        e.target.value = '';
    };

    const handlePaste = (e) => {
        const items = e.clipboardData && e.clipboardData.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type && items[i].type.indexOf('image') !== -1) {
                e.preventDefault();
                const file = items[i].getAsFile();
                handleFilePicked(file);
                return;
            }
        }
    };

    const removeAttachment = () => {
        setAttachmentWithPreview(null);
    };

    const canSend = text.trim().length > 0 && !sending && !disabled;

    const handleSend = async () => {
        const trimmed = text.trim();
        if (!trimmed) {
            // Body requerido: si intentó mandar solo un adjunto, pedimos un mensaje.
            if (attachment) setNeedsBodyHint(true);
            return;
        }
        if (sending || disabled) return;

        setSending(true);
        setErrorMessage('');
        const response = await onSend(trimmed, attachment);
        setSending(false);

        if (response && response.status === 'OK') {
            SupportChatService.clearDraft(draftKey);
            setText('');
            setAttachmentWithPreview(null);
            setNeedsBodyHint(false);
            if (typeof onSent === 'function') onSent(response);
            // Reenfocar para seguir la conversación.
            if (textareaRef.current) textareaRef.current.focus();
            return;
        }

        // Error: preservamos el texto en el textarea (no se pierde) y mostramos el mensaje.
        if (response && response.error_code === 'RATE_LIMITED') {
            setErrorMessage(t('support_chat.error_rate_limited'));
        } else if (response && response.status === 'ERROR' && response.error_message) {
            // Error de negocio con mensaje explícito del backend (ej. conversación de solo lectura):
            // lo mostramos tal cual, no lo pisamos con el genérico.
            setErrorMessage(response.error_message);
        } else {
            setErrorMessage(t('support_chat.error_network'));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="support-chat-composer">
            {errorMessage && (
                <div className="support-chat-composer__error" role="alert">{errorMessage}</div>
            )}
            {needsBodyHint && !errorMessage && (
                <div className="support-chat-composer__hint">{t('support_chat.attachment_needs_body')}</div>
            )}

            {attachment && previewUrl && (
                <div className="support-chat-composer__attachment">
                    <img src={previewUrl} alt={attachment.filename || ''} className="support-chat-composer__thumb"/>
                    <button
                        type="button"
                        className="support-chat-composer__attachment-remove"
                        onClick={removeAttachment}
                        aria-label={t('support_chat.remove_attachment')}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>
            )}

            <div className="support-chat-composer__row">
                <button
                    type="button"
                    className="support-chat-composer__clip"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={sending || disabled}
                    aria-label={t('support_chat.attach_image')}
                    title={t('support_chat.attach_image')}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES}
                    onChange={handleFileInputChange}
                    className="support-chat-composer__file-input"
                    aria-hidden="true"
                    tabIndex={-1}
                />

                <textarea
                    ref={textareaRef}
                    className="support-chat-composer__textarea"
                    value={text}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={t('support_chat.composer_placeholder')}
                    rows={1}
                    disabled={sending || disabled}
                    aria-label={t('support_chat.composer_placeholder')}
                />

                <button
                    type="button"
                    className="support-chat-composer__send"
                    onClick={handleSend}
                    disabled={!canSend}
                    aria-label={sending ? t('support_chat.sending') : t('support_chat.send')}
                    title={sending ? t('support_chat.sending') : t('support_chat.send')}
                >
                    {sending ? (
                        <span className="support-chat-composer__spinner" aria-hidden="true"/>
                    ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )}
                </button>
            </div>
        </div>
    );
};
